import React from 'react';
import { Card, Checkbox, Flex, Radio, Space, Tag, Tooltip, Typography } from 'antd';
import { LockOutlined, TagOutlined, TeamOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { ChannelEntity, ChannelType } from '../../../domain';
import { formatMembersCount } from '../../utils/formatMembersCount';

export interface ChannelCardProps {
  channel: ChannelEntity;
  mode: 'single' | 'multiple';
  showSelection: boolean;
  isSelected: boolean;
  onToggle: (uuid: string, selected: boolean) => void;
}

const ChannelCard: React.FC<ChannelCardProps> = ({ channel, mode, showSelection, isSelected, onToggle }) => {
  const { t } = useTranslation();
  const membersLabel = formatMembersCount(channel.membersCount);
  const isPaid = channel.type === ChannelType.PAID;

  return (
    <Card
      size="small"
      className={`channel-card${isSelected ? ' channel-card--selected' : ''}`}
      onClick={showSelection ? () => onToggle(channel.uuid, !isSelected) : undefined}
    >
      <Flex vertical gap={6}>
        <Flex justify="space-between" align="flex-start" gap="small">
          <Typography.Text strong ellipsis>{channel.title || channel.link}</Typography.Text>
          <Tag color={isPaid ? 'gold' : 'green'}>
            {t(isPaid ? 'channel-type-paid' : 'channel-type-free', { ns: 'labels' })}
          </Tag>
        </Flex>

        <Typography.Text type="secondary" ellipsis>{channel.link}</Typography.Text>

        <Flex justify="space-between" align="center" gap="small">
          <Space size={4} wrap>
            <Tag icon={<TagOutlined />} color={channel.category ? 'blue' : 'default'}>
              {channel.category?.name ?? t('channel-no-category', { ns: 'labels' })}
            </Tag>
            {membersLabel && (
              <Typography.Text type="secondary"><TeamOutlined /> {membersLabel}</Typography.Text>
            )}
            {channel.isPrivate && (
              <Tooltip title={t('private_channel', { ns: 'standard' })}>
                <LockOutlined style={{ color: 'rgba(0,0,0,.45)' }} />
              </Tooltip>
            )}
          </Space>

          {showSelection && (mode === 'single' ? (
            <Radio checked={isSelected} onClick={(e) => e.stopPropagation()} onChange={() => onToggle(channel.uuid, true)} />
          ) : (
            <Checkbox
              checked={isSelected}
              onClick={(e) => e.stopPropagation()}
              onChange={(e) => onToggle(channel.uuid, e.target.checked)}
            />
          ))}
        </Flex>
      </Flex>
    </Card>
  );
};

export default ChannelCard;
